import React, { createContext, useContext, useState, useEffect } from 'react';
import { trackLoginSuccess, trackLoginFailure, trackSignupSuccess, trackSignupFailure, trackLogout, setUserProps } from '../lib/analytics';
import { fetchWithRetry } from '../lib/fetchWithRetry';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Use environment variable or fallback to current origin for API URL
  const API_URL = window.location.origin;

  // Load user from localStorage on mount and validate token
  useEffect(() => {
    const validateAndRestoreSession = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (storedToken && storedUser) {
        try {
          // Validate token by calling /me endpoint
          const response = await fetchWithRetry(`${API_URL}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${storedToken}`
            }
          });
          
          if (response.ok) {
            const userData = await response.json();
            setToken(storedToken);
            setUser(userData);
          } else {
            // Token is invalid, clear storage
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        } catch (error) {
          // Network error - still try to use cached data
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
        }
      }
      setLoading(false);
    };
    
    validateAndRestoreSession();
  }, [API_URL]);

  const login = async (email, password) => {
    try {
      const response = await fetchWithRetry(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        let backendDetail = null;
        try {
          const data = await response.json();
          backendDetail = data?.detail || null;
        } catch (_) {
          // response wasn't JSON — fall through to generic message
        }
        if (response.status === 401 || response.status === 400) {
          throw new Error('Email or password incorrect. Please try again.');
        }
        // Preserve backend-supplied message for 403 (manual account),
        // 429 (lockout), etc.
        throw new Error(backendDetail || 'Login failed. Please try again.');
      }

      const data = await response.json();
      const { access_token, user: userData } = data;
      setToken(access_token);
      setUser(userData);
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));

      // Track successful login
      trackLoginSuccess('email');
      setUserProps({ user_role: userData.role, user_status: userData.status });

      return { success: true };
    } catch (error) {
      // Track failed login
      trackLoginFailure('email', error.message);
      // Provide user-friendly error message for network issues
      const errorMessage = error.message === 'Failed to fetch' 
        ? 'Unable to connect to server. Please check your internet connection and try again.'
        : error.message;
      return { success: false, error: errorMessage };
    }
  };

  const register = async (name, email, password, role = 'vendor', phone = '', stallNumber = '', firstName = '', lastName = '', vendorType = 'market', mainLocation = '') => {
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          name, 
          email, 
          password, 
          role, 
          phone, 
          stall_number: stallNumber,
          first_name: firstName,
          last_name: lastName,
          vendor_type: vendorType,
          main_location: mainLocation
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle duplicate email/user errors with friendly message
        const errorDetail = data.detail || '';
        if (typeof errorDetail === 'string' && (
            errorDetail.toLowerCase().includes('already registered') || 
            errorDetail.toLowerCase().includes('already exists') ||
            errorDetail.toLowerCase().includes('duplicate'))) {
          throw new Error('An account with this email already exists. Please log in instead.');
        }
        // Handle validation errors (e.g., password too short)
        if (Array.isArray(errorDetail)) {
          const msgs = errorDetail.map(e => e.msg).join('. ');
          throw new Error(msgs || 'Registration failed');
        }
        throw new Error(errorDetail || 'Registration failed');
      }

      // If vendor registration (pending approval)
      if (data.status === 'pending') {
        return { 
          success: true, 
          pending: true, 
          message: data.message 
        };
      }

      // If admin registration (auto-approved)
      if (data.access_token) {
        const { access_token, user: userData } = data;
        setToken(access_token);
        setUser(userData);
        
        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(userData));
      }

      // Track successful signup
      trackSignupSuccess('email');

      return { success: true, pending: false };
    } catch (error) {
      // Track failed signup
      trackSignupFailure('email', error.message);
      
      // Handle specific error messages with user-friendly alternatives
      let errorMessage = error.message;
      
      if (error.message === 'Failed to fetch') {
        errorMessage = 'Unable to connect to server. Please check your internet connection and try again.';
      } else if (error.message.includes('body stream already read') || 
                 error.message.includes('Failed to execute')) {
        errorMessage = 'An account with this email already exists. Please log in instead.';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    // Clear server-side cookie if available
    try {
      await fetch(`${API_URL}/api/auth/logout`, { method: 'POST' });
    } catch (e) {
      // Ignore network errors on logout
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Track logout
    trackLogout();
  };

  // Refresh user data from server (useful after payment/subscription changes)
  const refreshUser = async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) return;
    
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${storedToken}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        return userData;
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
    return null;
  };

  const value = {
    user,
    setUser,
    token,
    loading,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isVendor: user?.role === 'vendor',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
